import { ACCOUNT_FUNCTION_PATH, ACCOUNT_PROTOCOL_VERSION, checkAccountResponse, type AccountDeviceInfo, type AccountResponse } from '../../supabase/functions/_shared/accountProtocol';
import { devAccountWorld } from './fakeAccountWorld';

// Client transport to the account function (supabase/functions/account-v2). Same discipline as
// billingClient.ts: the user's JWT goes with every call, every outcome is a code the UI translates, and
// nothing here decides anything — the server binds the licence and rules on the device (Owner decision
// list 2026-10-19 item 50).
export type AccountFailureCode = 'offline' | 'network' | 'timeout' | 'notConfigured' | 'unauthenticated' | 'accountUnavailable' | 'malformedResponse';

export type AccountCallOutcome = { kind: 'ok'; response: Extract<AccountResponse, { ok: true }> } | { kind: 'failed'; code: AccountFailureCode; message: string };

export interface AccountTransport {
  status(token: string | null, device: AccountDeviceInfo): Promise<AccountCallOutcome>;
  bindLicense(token: string | null, device: AccountDeviceInfo, body: { licenseNumber: string; firstName: string; lastName: string }): Promise<AccountCallOutcome>;
}

export const ACCOUNT_CLIENT_TIMEOUT_MS = 20_000;

function endpoint(): string | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  return base.length > 0 ? `${base}${ACCOUNT_FUNCTION_PATH}` : null;
}

export function accountBackendConfigured(): boolean {
  return endpoint() !== null && ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim().length > 0;
}

async function call(action: 'status' | 'bindLicense', token: string | null, body: Record<string, unknown>): Promise<AccountCallOutcome> {
  const url = endpoint();
  const anon = ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
  if (!url || anon.length === 0) return { kind: 'failed', code: 'notConfigured', message: 'account backend is not configured' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { kind: 'failed', code: 'offline', message: 'device is offline' };
  if (!token) return { kind: 'failed', code: 'unauthenticated', message: 'no session token' };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ACCOUNT_CLIENT_TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: anon, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, protocolVersion: ACCOUNT_PROTOCOL_VERSION, ...body }),
      signal: controller.signal
    });
  } catch (raw) {
    clearTimeout(timer);
    const aborted = raw instanceof Error && raw.name === 'AbortError';
    return { kind: 'failed', code: aborted ? 'timeout' : 'network', message: aborted ? 'account request timed out' : 'network error' };
  }
  clearTimeout(timer);
  let parsed: unknown = null;
  try {
    parsed = (await res.json()) as unknown;
  } catch {
    parsed = null;
  }
  const checked = checkAccountResponse(parsed);
  if (!checked) return { kind: 'failed', code: res.status === 404 ? 'notConfigured' : 'malformedResponse', message: `unexpected answer (HTTP ${res.status})` };
  if (!checked.ok) return { kind: 'failed', code: checked.code === 'unauthenticated' ? 'unauthenticated' : 'accountUnavailable', message: checked.message };
  return { kind: 'ok', response: checked };
}

export const fetchAccountTransport: AccountTransport = {
  status: (token, device) => call('status', token, { device }),
  bindLicense: (token, device, body) => call('bindLicense', token, { device, ...body })
};

export const unavailableAccountTransport: AccountTransport = {
  status: async () => ({ kind: 'failed', code: 'notConfigured', message: 'account backend is not configured' }),
  bindLicense: async () => ({ kind: 'failed', code: 'notConfigured', message: 'account backend is not configured' })
};

let transportOverride: AccountTransport | null = null;

export function setAccountTransportForTests(transport: AccountTransport | null): void {
  transportOverride = transport;
}

export function getAccountTransport(): AccountTransport {
  if (transportOverride) return transportOverride;
  if (accountBackendConfigured()) return fetchAccountTransport;
  // DEVELOPMENT ONLY: the browser dev server without a backend runs against the in-memory world.
  if (import.meta.env.DEV) return devAccountWorld().transport;
  return unavailableAccountTransport;
}
